// Stage Mail-1 / Mail-3:Mail account / Mail message 関連 API。
//
// DTO ルール:
// - mail_account_out は password / *_password_encrypted を含まない
//   (Mail-1 §3.3)
// - mail_message_out は direction / status / is_read / is_important のみ。
//   同期メタ (imap_uid / uidvalidity / imap_identity / mailbox_name) は含まれない
//   (Mail-3 §5.3)
// - tenant_id は response に絶対含めない(server-side 决定)

import { apiFetch } from "./client";

export interface MailAccount {
  id: string;
  name: string;
  email_address: string;

  imap_host: string;
  imap_port: number;
  imap_security: "ssl" | "starttls" | "none";
  imap_username: string;
  imap_password_hint: string;
  has_imap_password: boolean;

  smtp_host: string;
  smtp_port: number;
  smtp_security: "ssl" | "starttls" | "none";
  smtp_username: string;
  smtp_password_hint: string;
  has_smtp_password: boolean;

  is_active: boolean;
  account_type?: "personal" | "shared" | "other";
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Mail-V2.5 (2026-08-19): 自動作成された initial sync job の id。
   *  POST /api/mail/accounts (V1) と POST /api/mail-v2/accounts (V2) の
   *  response body に含まれる。null = 自動起動失敗 or job 既に取られた。 */
  sync_job_id?: string | null;
}

export type MailSecurity = "ssl" | "starttls" | "none";

export interface MailSecurityBlock {
  host: string;
  port: number;
  security: MailSecurity;
  username: string;
  /** Password は作成 / 更新時のみ送信。GET response には含まれない。
   *  GET 側で password を渡すと backend は新規 password として encrypt する。
   *  null = 「既存値保持」、undefined = 「更新不要」 */
  password?: string | null;
}

export interface MailAccountCreateBody {
  name: string;
  email_address: string;
  imap: MailSecurityBlock;
  smtp: MailSecurityBlock;
  is_active?: boolean;
  account_type?: "personal" | "shared" | "other";
}

export type MailAccountUpdateBody = MailAccountCreateBody;

export type MailTestMode = "input" | "stored";

export interface MailTestResult {
  status: "connected" | "auth_failed" | "not_found" | "timeout" | "error";
  message: string;
  /** Provider 表示キー:"imap" / "smtp" */
  provider: "imap" | "smtp";
  /** 試行モード echo。"input" / "stored" */
  mode: MailTestMode;
  /** Server side 戻り値:対象の account_id */
  account_id?: string;
  /** 例外クラス名(失敗時のみ付与) */
  error_class?: string;
  /** provider="imap" 時の scrubbed エラー文字列(失敗時のみ付与) */
  imap_error?: string;
  /** provider="smtp" 時の scrubbed エラー文字列(失敗時のみ付与、imap_error の alias) */
  smtp_error?: string;
}

export interface MailTestInputBody {
  mode: "input";
  config: MailSecurityBlock;
}

export interface MailTestStoredBody {
  mode: "stored";
}

export type MailTestBody = MailTestInputBody | MailTestStoredBody;

// ============================================================
// Stage Mail-3: messages (inbound + outbound は来たら表示)
// ============================================================

/** Mail-3 §5.2 状態 machine value → frontend 表示変換は i18n 層で見る */
export type MailStatus = "unhandled" | "handling" | "completed";

export type MailDirection = "inbound" | "outbound";

export interface MailMessage {
  id: string;
  mail_account_id: string;
  direction: MailDirection;
  from_name: string | null;
  from_email: string;
  to_addresses: string;
  subject: string | null;
  date_header: string | null;
  body_text: string | null;
  body_html_sanitized: string | null;
  is_read: boolean;
  is_important: boolean;
  status: MailStatus;
  received_at: string | null;
  created_at: string;
  message_id: string | null;
}

/** GET /api/mail/messages response */
export interface MailMessageListResponse {
  items: MailMessage[];
  total: number;
  page: number;
  page_size: number;
}

/** GET /api/mail/messages クエリ */
export interface MailMessageListQuery {
  status?: MailStatus;
  is_read?: boolean;
  is_important?: boolean;
  page?: number;
  page_size?: number;
}

/** PATCH /api/mail/messages/<id> body — status / is_read / is_important 単体送信 OK */
export interface MailMessagePatchBody {
  status?: MailStatus;
  is_read?: boolean;
  is_important?: boolean;
}

/** GET /api/mail/unread-count response */
export interface MailUnreadCount {
  unread: number;
}

/** query オブジェクト → URLSearchParams 変換ヘルパー */
function _buildMessageListQs(q?: MailMessageListQuery): string {
  if (!q) return "";
  const sp = new URLSearchParams();
  if (q.status) sp.set("status", q.status);
  if (q.is_read !== undefined) sp.set("is_read", String(q.is_read));
  if (q.is_important !== undefined) sp.set("is_important", String(q.is_important));
  if (q.page !== undefined) sp.set("page", String(q.page));
  if (q.page_size !== undefined) sp.set("page_size", String(q.page_size));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const mailApi = {
  // ── Mail-1 ──
  listAccounts: () => apiFetch<MailAccount[]>("/mail/accounts"),
  getAccount: (id: string) => apiFetch<MailAccount>(`/mail/accounts/${id}`),
  createAccount: (body: MailAccountCreateBody) =>
    apiFetch<MailAccount>("/mail/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAccount: (id: string, body: MailAccountUpdateBody) =>
    apiFetch<MailAccount>(`/mail/accounts/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  disconnectAccount: (id: string) =>
    apiFetch<{ status: "disconnected" }>(`/mail/accounts/${id}`, {
      method: "DELETE",
    }),
  testImap: (id: string, body: MailTestBody) =>
    apiFetch<MailTestResult>(`/mail/accounts/${id}/test-imap`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  testSmtp: (id: string, body: MailTestBody) =>
    apiFetch<MailTestResult>(`/mail/accounts/${id}/test-smtp`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  testImapInput: (body: MailTestBody) =>
    apiFetch<MailTestResult>("/mail/accounts/test-imap", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  testSmtpInput: (body: MailTestBody) =>
    apiFetch<MailTestResult>("/mail/accounts/test-smtp", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Mail-2 sync helper ──
  syncAccount: (id: string) =>
    apiFetch<{
      mail_account_id: string;
      mailbox_name: string;
      status: string;
      message: string;
      count_inserted: number;
      count_skipped: number;
      last_seen_uid: number;
      uidvalidity: number | null;
    }>(`/mail/accounts/${id}/sync`, { method: "POST" }),

  // ── Mail-3 ──
  listMessages: (query?: MailMessageListQuery) =>
    apiFetch<MailMessageListResponse>(`/mail/messages${_buildMessageListQs(query)}`),

  getMessage: (id: string) => apiFetch<MailMessage>(`/mail/messages/${id}`),

  patchMessage: (id: string, body: MailMessagePatchBody) =>
    apiFetch<MailMessage>(`/mail/messages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  unreadCount: () => apiFetch<MailUnreadCount>("/mail/unread-count"),

  // ── Mail-4B: capability endpoint ──
  getCapability: () =>
    apiFetch<{ ai_reply_enabled: boolean }>("/mail/capability"),

  // ── Mail-4B: AI 返信ドラフト(design §7.2) ──
  aiReply: (
    messageId: string,
    body: { instruction?: string; tone?: "polite" | "friendly"; thread_limit?: number }
  ) =>
    apiFetch<{
      draft_text: string | null;
      finish_reason: string;
      usage: { total_tokens: number | null };
      duration_ms: number;
    }>(`/mail/messages/${messageId}/ai-reply`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
